import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface WebSocketMessage {
  type: 'connected' | 'new_order' | 'order_status_update' | 'pong';
  order_id?: number;
  employee_name?: string;
  employee_number?: string;
  order_type?: string;
  meal_type?: 'breakfast' | 'lunch';
  order_date?: string;
  old_status?: string;
  new_status?: string;
  timestamp?: string;
  message?: string;
  staff?: {
    staff_id: number;
    username: string;
    display_name: string;
    can_view_orders: boolean;
    can_update_status: boolean;
  };
}

interface UseKitchenWebSocketOptions {
  enabled?: boolean;
  onNewOrder?: (message: WebSocketMessage) => void;
  onOrderStatusUpdate?: (message: WebSocketMessage) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

interface UseKitchenWebSocketReturn {
  isConnected: boolean;
  connectionError: string | null;
  lastMessage: WebSocketMessage | null;
}

const WS_BASE_URL =
  (import.meta as any).env?.VITE_WS_URL ||
  `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
const RECONNECT_DELAY = 3000; // 3 seconds
const PING_INTERVAL = 30000; // 30 seconds

export function useKitchenWebSocket(
  options: UseKitchenWebSocketOptions = {}
): UseKitchenWebSocketReturn {
  const { enabled = true, onNewOrder, onOrderStatusUpdate, onConnected, onDisconnected } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldReconnectRef = useRef(true);

  // Store callbacks in refs to avoid reconnection on callback changes
  const onNewOrderRef = useRef(onNewOrder);
  const onOrderStatusUpdateRef = useRef(onOrderStatusUpdate);
  const onConnectedRef = useRef(onConnected);
  const onDisconnectedRef = useRef(onDisconnected);

  // Update refs when callbacks change (without triggering reconnect)
  useEffect(() => {
    onNewOrderRef.current = onNewOrder;
    onOrderStatusUpdateRef.current = onOrderStatusUpdate;
    onConnectedRef.current = onConnected;
    onDisconnectedRef.current = onDisconnected;
  }, [onNewOrder, onOrderStatusUpdate, onConnected, onDisconnected]);

  const queryClient = useQueryClient();

  // Cleanup function
  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!enabled) return;

    // Get kitchen JWT token from sessionStorage (set by AuthProvider)
    const token = sessionStorage.getItem('hmh_access_token');
    if (!token) {
      setConnectionError('No authentication token found');
      return;
    }

    try {
      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.close();
      }

      // Create WebSocket connection with token as query parameter
      const ws = new WebSocket(`${WS_BASE_URL}/ws/kitchen?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Kitchen WebSocket connected');
        setIsConnected(true);
        setConnectionError(null);
        onConnectedRef.current?.();

        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          setLastMessage(message);

          // Handle different message types
          switch (message.type) {
            case 'connected':
              console.log('Kitchen WebSocket authentication successful', message.staff);
              break;

            case 'new_order':
              console.log('New order received:', message);
              onNewOrderRef.current?.(message);

              // Invalidate orders query to refresh the list
              queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
              queryClient.invalidateQueries({ queryKey: ['admin', 'breakfast-orders'] });
              break;

            case 'order_status_update':
              console.log('Order status updated:', message);
              onOrderStatusUpdateRef.current?.(message);

              // Invalidate orders query to refresh the list
              queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
              queryClient.invalidateQueries({ queryKey: ['admin', 'breakfast-orders'] });
              break;

            case 'pong':
              // Heartbeat response - connection is alive
              break;

            default:
              console.log('Unknown message type:', message.type);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('Kitchen WebSocket error:', error);
        setConnectionError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        console.log('Kitchen WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        onDisconnectedRef.current?.();

        // Clean up ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Attempt to reconnect if not a normal closure and we should reconnect
        if (event.code !== 1000 && shouldReconnectRef.current && enabled) {
          console.log(`Attempting to reconnect in ${RECONNECT_DELAY / 1000} seconds...`);
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, RECONNECT_DELAY);
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      setConnectionError('Failed to create WebSocket connection');
    }
  }, [enabled, queryClient]);

  // Connect on mount and when enabled changes
  useEffect(() => {
    if (enabled) {
      shouldReconnectRef.current = true;
      connect();
    } else {
      shouldReconnectRef.current = false;
      cleanup();
    }

    // Cleanup on unmount
    return () => {
      shouldReconnectRef.current = false;
      cleanup();
    };
  }, [enabled, connect, cleanup]);

  return {
    isConnected,
    connectionError,
    lastMessage,
  };
}
